import { jsPDF } from 'jspdf';

export type PdfTableColumn = {
  header: string;
  key: string;
  width?: number;
};

/**
 * Gera e baixa um PDF tabular simples (paisagem A4) a partir de dados em memória.
 */
export function exportTablePdf(
  title: string,
  columns: PdfTableColumn[],
  rows: Record<string, unknown>[],
  fileName: string,
  footer?: string
): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const marginX = 28;
  const startY = 40;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const usableWidth = pageWidth - marginX * 2;

  const totalConfigured = columns.reduce((acc, c) => acc + (c.width ?? 0), 0);
  const autoCount = columns.filter(c => !c.width).length;
  const remaining = Math.max(usableWidth - totalConfigured, 40);
  const autoWidth = autoCount > 0 ? remaining / autoCount : 0;
  const widths = columns.map(c => c.width ?? autoWidth);

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(title, pageWidth / 2, startY, { align: 'center' });

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `Emitido em: ${new Date().toLocaleString('pt-BR')}`,
    pageWidth / 2,
    startY + 16,
    { align: 'center' }
  );

  let y = startY + 36;
  const rowHeight = 16;

  const drawHeader = () => {
    doc.setFont('helvetica', 'bold');
    doc.setFillColor(241, 245, 249);
    doc.rect(marginX, y - 11, usableWidth, rowHeight, 'F');
    let x = marginX;
    columns.forEach((col, i) => {
      doc.text(String(col.header), x + 2, y);
      x += widths[i];
    });
    y += rowHeight;
    doc.setFont('helvetica', 'normal');
  };

  drawHeader();

  rows.forEach(row => {
    if (y > pageHeight - 40) {
      doc.addPage();
      y = 40;
      drawHeader();
    }

    let x = marginX;
    columns.forEach((col, i) => {
      const raw = row[col.key];
      const text = raw == null || raw === '' ? '---' : String(raw);
      const clipped = doc.splitTextToSize(text, widths[i] - 4)[0] ?? '';
      doc.text(clipped, x + 2, y);
      x += widths[i];
    });
    y += rowHeight;
  });

  if (footer) {
    if (y > pageHeight - 36) {
      doc.addPage();
      y = 40;
    }
    doc.setFont('helvetica', 'bold');
    doc.text(footer, marginX, y + 12);
  }

  const safeName = fileName.replace(/\.pdf$/i, '');
  doc.save(`${safeName}.pdf`);
}
